import type { MarketplaceCcgPolicyProjection, MarketplaceCcgLimits } from './marketplace';
import {
  EBAY_PROFESSIONAL_GRADE_IDS,
  EBAY_PROFESSIONAL_GRADER_IDS,
  EBAY_RAW_CONDITION_IDS,
  normalizeCardPresentation,
} from './card-presentation';
import type { CardPresentation } from './card-presentation';
import type { FulfillmentTaskState } from './fulfillment';
import type { CardIdentity } from './types';
import { CARD_LANGUAGES } from './card-language';
import type { EbayCategoryPolicy } from './ebay-category-policy';
import {
  EBAY_CCG_SINGLES_HEADERS,
  mapEbayCategoryPresentation,
  requireEbayCcgPublicPolicy,
  validateEbayCcgPublicSpecifics,
} from './ebay-ccg-seller-hub';

export const EBAY_SELLER_HUB_TEMPLATE_VERSION = '1.0.0';
export const EBAY_SELLER_HUB_ACTION_VERSION = '1193';
export const EBAY_SELLER_HUB_EXPORT_LIMIT = 99;

export const EBAY_US_SELLER_POLICIES = {
  payment: { name: 'Default Payment Poiicy', id: '257829089018' },
  return: { name: 'Free Returns 30 Days', id: '257829088018' },
  shipping: {
    PWE: { name: 'PWE', id: '268586972018' },
    USPS_GROUND: { name: 'USPS Ground', id: '262908712018' },
  },
} as const;

export type EbaySellerHubSinglesCategory = 'NON_SPORT_SINGLES' | 'SPORT_SINGLES' | 'CCG_SINGLES';
export type EbaySellerHubCategorySelection = 'AUTO' | EbaySellerHubSinglesCategory;
export type EbaySellerHubListingType = 'BUY_IT_NOW' | 'AUCTION';
export type EbaySellerHubPriceSource = 'SALE_PRICE' | 'CALCULATED_PRICE' | 'PRICE_OVERRIDE';
export type EbayItemSpecifics = Record<string, string[]>;

export interface EbayItemSpecificFieldSchema {
  name: string;
  required: boolean;
  multiple: boolean;
  suggestedValues: readonly string[];
}

export interface EbaySellerHubExportSettings {
  category: EbaySellerHubCategorySelection;
  listingType: EbaySellerHubListingType;
  priceSource: EbaySellerHubPriceSource;
  priceOverrideMinor?: number;
  bestOfferEnabled: boolean;
  bestOfferAutoAcceptPercent?: number;
  bestOfferAutoDeclinePercent?: number;
  location: string;
  paymentProfileName: string;
  paymentProfileId: string;
  shippingProfileName: string;
  shippingProfileId: string;
  returnProfileName: string;
  returnProfileId: string;
  franchise?: string;
}

export interface EbaySellerHubResolvedExportSettings extends EbaySellerHubExportSettings {
  category: EbaySellerHubSinglesCategory;
}

export interface EbaySellerHubExportCard {
  cardId: string;
  shortCode?: string;
  marketplaceSku: string;
  title: string;
  description?: string;
  amountMinor: number;
  identity: CardIdentity;
  imageUrls: string[];
  cardPresentation?: CardPresentation;
  itemSpecifics?: EbayItemSpecifics;
  /**
   * Language observed at import when the linked catalog row does not state one (RFC-0004 R1 evidence). Used for
   * the Language item specific only when `identity.language` is absent; it never becomes identity.
   */
  observedLanguage?: string;
}

/** Listing language: the catalog fact first, then the import-observed language. */
export function ebayListingLanguage(
  card: Pick<EbaySellerHubExportCard, 'identity' | 'observedLanguage'>,
): string | undefined {
  return card.identity.language?.trim() || card.observedLanguage?.trim() || undefined;
}

export interface EbaySellerHubExportItem {
  cardId: string;
  shortCode?: string;
  marketplaceSku: string;
  title: string;
  errors: string[];
  warnings: string[];
  conflicts?: EbaySellerHubExportConflict[];
}

export type EbaySellerHubExportConflictType = 'PENDING_EXPORT' | 'ACTIVE_LISTING' | 'SOLD_LISTING';

export interface EbaySellerHubExportConflict {
  type: EbaySellerHubExportConflictType;
  message: string;
  observedState?: string;
  externalListingId?: string;
  exportFingerprint?: string;
  occurredAt?: string;
  operatorId?: string;
}

export interface EbaySellerHubExportPreviewResponse {
  ccgPolicy?: MarketplaceCcgPolicyProjection;
  limits?: MarketplaceCcgLimits;
  fingerprint: string;
  filename: string;
  valid: boolean;
  items: EbaySellerHubExportItem[];
  csv?: string;
}

export interface EbaySellerHubExportRequest {
  operation: 'SELLER_HUB_PREVIEW' | 'SELLER_HUB_GENERATE';
  cardIds: string[];
  settings: EbaySellerHubExportSettings;
}

export interface EbaySellerHubExportReleaseRequest {
  fingerprint: string;
}

export interface EbaySellerHubExportDownloadRequest {
  fingerprint: string;
}

export interface EbaySellerHubExportDownloadResponse {
  fingerprint: string;
  filename: string;
  csv: string;
}

export type MarketplaceListingActivityItemStatus =
  | 'AWAITING_RESULT'
  | 'LISTED'
  | 'REJECTED'
  | 'RETURNED_TO_QUEUE'
  | 'RESOLVED';

export type MarketplaceListingActivityStatus =
  | 'PENDING'
  | 'PARTIALLY_RETURNED'
  | 'ACTION_REQUIRED'
  | 'RETURNED'
  | 'RESOLVED';

export type MarketplaceListingActivityEbayStatus =
  | 'ACTIVE'
  | 'SOLD'
  | 'ENDED'
  | 'MISSING'
  | 'UNKNOWN';

export interface MarketplaceListingActivityItem {
  cardId: string;
  marketplaceSku: string;
  title: string;
  status: MarketplaceListingActivityItemStatus;
  returnedAt?: string;
  returnedBy?: string;
  returnReason?: string;
  ebayItemId?: string;
  ebayStatus?: MarketplaceListingActivityEbayStatus;
  soldAt?: string;
  soldAmountMinor?: number;
  soldCurrency?: string;
  pickStatus?: FulfillmentTaskState;
  resultAt?: string;
  resultMessage?: string;
  errorCode?: string;
}

export interface MarketplaceListingActivityGroup {
  activityId: string;
  marketplace: 'EBAY_US';
  method: 'SELLER_HUB_CSV';
  status: MarketplaceListingActivityStatus;
  fingerprint: string;
  filename: string;
  createdAt: string;
  createdBy?: string;
  items: MarketplaceListingActivityItem[];
}

export interface ListMarketplaceListingActivitiesRequest {
  operation: 'LIST_MARKETPLACE_LISTING_ACTIVITIES';
  cursor?: string;
  limit?: number;
}

export interface ListMarketplaceListingActivitiesResponse {
  items: MarketplaceListingActivityGroup[];
  nextToken?: string;
}

export type MarketplaceListingActivityReturnReason =
  | 'FILE_NOT_UPLOADED'
  | 'ROWS_REJECTED'
  | 'NO_ACTIVE_LISTING_CONFIRMED';

export interface ReturnMarketplaceListingActivityItemsRequest {
  operation: 'RETURN_MARKETPLACE_LISTING_ACTIVITY_ITEMS';
  fingerprint: string;
  cardIds: string[];
  reason: MarketplaceListingActivityReturnReason;
}

export interface ReturnMarketplaceListingActivityItemsResponse {
  fingerprint: string;
  status: MarketplaceListingActivityStatus;
  returnedCardIds: string[];
}

export type EbaySellerHubResultOutcome = 'SUCCESS' | 'FAILED' | 'UNKNOWN';

export interface EbaySellerHubResultRow {
  rowNumber: number;
  marketplaceSku: string;
  outcome: EbaySellerHubResultOutcome;
  rawStatus: string;
  ebayItemId?: string;
  errorCode?: string;
  message?: string;
}

export interface EbaySellerHubResultPreviewItem extends EbaySellerHubResultRow {
  cardId?: string;
  disposition: 'LISTED' | 'REJECTED' | 'AWAITING_RESULT' | 'CONFLICT' | 'NOT_IN_EXPORT';
  issue?: string;
}

export interface EbaySellerHubResultSummary {
  listed: number;
  rejected: number;
  awaitingResult: number;
  conflicts: number;
  notInExport: number;
}

export interface EbaySellerHubResultRequest {
  operation: 'SELLER_HUB_RESULTS_PREVIEW' | 'SELLER_HUB_RESULTS_APPLY';
  fingerprint: string;
  sourceFilename: string;
  csv: string;
  resultFingerprint?: string;
}

export interface EbaySellerHubResultResponse {
  exportFingerprint: string;
  resultFingerprint: string;
  sourceFilename: string;
  summary: EbaySellerHubResultSummary;
  items: EbaySellerHubResultPreviewItem[];
  applied: boolean;
}

function parseCsvRecords(text: string): string[][] {
  const records: string[][] = [];
  let field = '';
  let record: string[] = [];
  let inQuotes = false;
  let started = false;
  const pushField = (): void => {
    record.push(field);
    field = '';
  };
  const finishRecord = (): void => {
    pushField();
    records.push(record);
    record = [];
    started = false;
  };
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index] as string;
    if (inQuotes) {
      if (character === '"' && text[index + 1] === '"') {
        field += '"';
        index += 1;
      } else if (character === '"') {
        inQuotes = false;
      } else {
        field += character;
      }
    } else if (character === '"') {
      inQuotes = true;
      started = true;
    } else if (character === ',') {
      pushField();
      started = true;
    } else if (character === '\n' || character === '\r') {
      if (character === '\r' && text[index + 1] === '\n') index += 1;
      if (started || field !== '') finishRecord();
    } else {
      field += character;
      started = true;
    }
  }
  if (started || field !== '') finishRecord();
  return records;
}

function normalizedResultHeader(value: string): string {
  return value
    .replace(/^\uFEFF/, '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '');
}

function resultColumn(header: readonly string[], aliases: readonly string[]): number {
  const normalizedAliases = aliases.map(normalizedResultHeader);
  return header.findIndex((value) => normalizedAliases.includes(value));
}

/** Parse a Seller Hub upload processing report. Unknown statuses remain explicit and fail closed. */
export function parseEbaySellerHubResultCsv(text: string): EbaySellerHubResultRow[] {
  const records = parseCsvRecords(text);
  const header = records.shift()?.map(normalizedResultHeader) ?? [];
  const skuIndex = resultColumn(header, [
    'Custom label (SKU)',
    'CustomLabel',
    'Custom label',
    'SKU',
  ]);
  const statusIndex = resultColumn(header, ['Status', 'Result', 'Result status']);
  if (skuIndex === -1 || statusIndex === -1)
    throw new Error('ebay_seller_hub_result_columns_required');
  const itemIdIndex = resultColumn(header, ['Item ID', 'ItemID', 'Item number', 'ItemNumber']);
  const errorCodeIndex = resultColumn(header, ['Error code', 'ErrorCode']);
  const messageIndex = resultColumn(header, [
    'Error message',
    'ErrorMessage',
    'Message',
    'Details',
  ]);
  const textAt = (cells: readonly string[], column: number): string | undefined => {
    if (column === -1) return undefined;
    const value = (cells[column] ?? '').trim();
    return value === '' ? undefined : value;
  };
  return records
    .map((cells, index): EbaySellerHubResultRow | undefined => {
      const marketplaceSku = textAt(cells, skuIndex);
      if (marketplaceSku === undefined) return undefined;
      const rawStatus = textAt(cells, statusIndex) ?? '';
      const status = rawStatus
        .toLowerCase()
        .replace(/[^a-z]+/g, ' ')
        .trim();
      const ebayItemId = textAt(cells, itemIdIndex);
      const success = /^(success|successful|complete|completed|ok|warning)$/.test(status);
      const failed = /^(error|failed|failure|rejected|invalid)$/.test(status);
      return {
        rowNumber: index + 2,
        marketplaceSku,
        outcome: success && ebayItemId !== undefined ? 'SUCCESS' : failed ? 'FAILED' : 'UNKNOWN',
        rawStatus,
        ...(ebayItemId === undefined ? {} : { ebayItemId }),
        ...(textAt(cells, errorCodeIndex) === undefined
          ? {}
          : { errorCode: textAt(cells, errorCodeIndex) }),
        ...(textAt(cells, messageIndex) === undefined
          ? {}
          : { message: textAt(cells, messageIndex) }),
      };
    })
    .filter((row): row is EbaySellerHubResultRow => row !== undefined);
}

const ACTION_HEADER = `*Action(SiteID=US|Country=US|Currency=USD|Version=${EBAY_SELLER_HUB_ACTION_VERSION}|CC=UTF-8)`;

export const EBAY_NON_SPORT_SINGLES_HEADERS = [
  ACTION_HEADER,
  'CustomLabel',
  '*Category',
  'StoreCategory',
  '*Title',
  'Subtitle',
  'Relationship',
  'RelationshipDetails',
  'ScheduleTime',
  '*ConditionID',
  'CD:Professional Grader - (ID: 27501)',
  'CD:Grade - (ID: 27502)',
  'CDA:Certification Number - (ID: 27503)',
  'CD:Card Condition - (ID: 40001)',
  '*C:Franchise',
  'C:Manufacturer',
  'C:Autograph Authentication',
  'C:Year Manufactured',
  'C:Set',
  'C:Character',
  'C:TV Show',
  'C:Features',
  'C:Parallel/Variety',
  'C:Featured Person/Artist',
  'C:Autographed',
  'C:Type',
  'C:Card Number',
  'C:Card Name',
  'C:Movie',
  'C:Age Level',
  'C:Signed By',
  'C:Genre',
  'C:Material',
  'C:Illustrator',
  'C:Autograph Format',
  'C:Language',
  'C:Card Size',
  'C:Country of Origin',
  'C:Original/Licensed Reprint',
  'C:Vintage',
  'C:Convention/Event',
  'C:California Prop 65 Warning',
  'C:Autograph Authentication Number',
  'C:Card Thickness',
  'C:Customized',
  'C:Insert Set',
  'C:Print Run',
  'PicURL',
  'GalleryType',
  'VideoID',
  '*Description',
  '*Format',
  '*Duration',
  '*StartPrice',
  'BuyItNowPrice',
  'BestOfferEnabled',
  'BestOfferAutoAcceptPrice',
  'MinimumBestOfferPrice',
  '*Quantity',
  'ImmediatePayRequired',
  '*Location',
  'ShippingType',
  'ShippingService-1:Option',
  'ShippingService-1:Cost',
  'ShippingService-2:Option',
  'ShippingService-2:Cost',
  '*DispatchTimeMax',
  'PromotionalShippingDiscount',
  'ShippingDiscountProfileID',
  '*ReturnsAcceptedOption',
  'ReturnsWithinOption',
  'RefundOption',
  'ShippingCostPaidByOption',
  'AdditionalDetails',
  'ShippingProfileID',
  'ShippingProfileName',
  'ReturnProfileID',
  'ReturnProfileName',
  'PaymentProfileID',
  'PaymentProfileName',
  'ProductCompliancePolicyID',
  'Regional ProductCompliancePolicies',
  'Product Safety Pictograms',
  'Product Safety Statements',
  'Product Safety Component',
  'Regulatory Document Ids',
  'Manufacturer Name',
  'Manufacturer AddressLine1',
  'Manufacturer AddressLine2',
  'Manufacturer City',
  'Manufacturer Country',
  'Manufacturer PostalCode',
  'Manufacturer StateOrProvince',
  'Manufacturer Phone',
  'Manufacturer Email',
  'Manufacturer ContactURL',
  'Responsible Person 1',
  'Responsible Person 1 Type',
  'Responsible Person 1 AddressLine1',
  'Responsible Person 1 AddressLine2',
  'Responsible Person 1 City',
  'Responsible Person 1 Country',
  'Responsible Person 1 PostalCode',
  'Responsible Person 1 StateOrProvince',
  'Responsible Person 1 Phone',
  'Responsible Person 1 Email',
  'Responsible Person 1 ContactURL',
] as const;

export const EBAY_SPORT_SINGLES_HEADERS = [
  ACTION_HEADER,
  'CustomLabel',
  '*Category',
  'StoreCategory',
  '*Title',
  'Subtitle',
  'Relationship',
  'RelationshipDetails',
  'ScheduleTime',
  '*ConditionID',
  'CD:Professional Grader - (ID: 27501)',
  'CD:Grade - (ID: 27502)',
  'CDA:Certification Number - (ID: 27503)',
  'CD:Card Condition - (ID: 40001)',
  '*C:Sport',
  'C:Player/Athlete',
  'C:Manufacturer',
  'C:Season',
  'C:Parallel/Variety',
  'C:Features',
  'C:Set',
  'C:Team',
  'C:League',
  'C:Autographed',
  'C:Card Name',
  'C:Card Number',
  'C:Type',
  'C:Year Manufactured',
  'C:Signed By',
  'C:Autograph Authentication',
  'C:Card Size',
  'C:Country of Origin',
  'C:Material',
  'C:Autograph Format',
  'C:Vintage',
  'C:Event/Tournament',
  'C:Language',
  'C:Original/Licensed Reprint',
  'C:Autograph Authentication Number',
  'C:California Prop 65 Warning',
  'C:Card Thickness',
  'C:Customized',
  'C:Insert Set',
  'C:Print Run',
  'PicURL',
  'GalleryType',
  'VideoID',
  '*Description',
  '*Format',
  '*Duration',
  '*StartPrice',
  'BuyItNowPrice',
  'BestOfferEnabled',
  'BestOfferAutoAcceptPrice',
  'MinimumBestOfferPrice',
  '*Quantity',
  'ImmediatePayRequired',
  '*Location',
  'ShippingType',
  'ShippingService-1:Option',
  'ShippingService-1:Cost',
  'ShippingService-2:Option',
  'ShippingService-2:Cost',
  '*DispatchTimeMax',
  'PromotionalShippingDiscount',
  'ShippingDiscountProfileID',
  '*ReturnsAcceptedOption',
  'ReturnsWithinOption',
  'RefundOption',
  'ShippingCostPaidByOption',
  'AdditionalDetails',
  'ShippingProfileID',
  'ShippingProfileName',
  'ReturnProfileID',
  'ReturnProfileName',
  'PaymentProfileID',
  'PaymentProfileName',
  'ProductCompliancePolicyID',
  'Regional ProductCompliancePolicies',
  'Product Safety Pictograms',
  'Product Safety Statements',
  'Product Safety Component',
  'Regulatory Document Ids',
  'Manufacturer Name',
  'Manufacturer AddressLine1',
  'Manufacturer AddressLine2',
  'Manufacturer City',
  'Manufacturer Country',
  'Manufacturer PostalCode',
  'Manufacturer StateOrProvince',
  'Manufacturer Phone',
  'Manufacturer Email',
  'Manufacturer ContactURL',
  'Responsible Person 1',
  'Responsible Person 1 Type',
  'Responsible Person 1 AddressLine1',
  'Responsible Person 1 AddressLine2',
  'Responsible Person 1 City',
  'Responsible Person 1 Country',
  'Responsible Person 1 PostalCode',
  'Responsible Person 1 StateOrProvince',
  'Responsible Person 1 Phone',
  'Responsible Person 1 Email',
  'Responsible Person 1 ContactURL',
] as const;

export const EBAY_SPORT_CARD_FEATURES = [
  '1st Edition',
  'Chase',
  'Collectors Edition',
  'Exclusive',
  'Hologram',
  'Insert',
  'Limited Edition',
  'Memorabilia',
  'One of One',
  'Parallel/Variety',
  'Printing Plate',
  'Promo',
  'Rookie',
  'Serial Numbered',
  'Short Print',
  'Sketch',
  'Sticker',
] as const;

export const EBAY_NON_SPORT_CARD_FEATURES = [
  '1st Edition',
  'Chase',
  'Collectors Edition',
  'Exclusive',
  'Hologram',
  'Insert',
  'Limited Edition',
  'Memorabilia',
  'One of One',
  'Parallel/Variety',
  'Printing Plate',
  'Promo',
  'Serial Numbered',
  'Short Print',
  'Sketch',
  'Sticker',
] as const;

const ITEM_SPECIFIC_SUGGESTIONS: Readonly<Record<string, readonly string[]>> = {
  Autographed: ['Yes', 'No'],
  Customized: ['Yes', 'No'],
  Vintage: ['Yes', 'No'],
  Language: CARD_LANGUAGES,
  'Original/Licensed Reprint': ['Original', 'Licensed Reprint'],
  Type: ['Sports Trading Card', 'Non-Sport Trading Card'],
};

function itemSpecificName(header: string): string | undefined {
  const match = /^\*?C:(.+)$/.exec(header);
  return match?.[1];
}

/** Bounded category schema derived from the checked-in official Seller Hub templates. */
export function ebayItemSpecificSchema(
  category: EbaySellerHubSinglesCategory,
  policy?: EbayCategoryPolicy,
): EbayItemSpecificFieldSchema[] {
  if (category === 'CCG_SINGLES')
    return requireEbayCcgPublicPolicy(policy).aspects.map((field) => ({
      name: field.name,
      required: field.required,
      multiple: field.cardinality === 'MULTI',
      suggestedValues:
        field.mode === 'SELECTION_ONLY' ? (field.selectionValues ?? []) : field.suggestions,
    }));
  return ebaySellerHubHeaders(category).flatMap((header) => {
    const name = itemSpecificName(header);
    if (name === undefined) return [];
    return [
      {
        name,
        required: header.startsWith('*C:'),
        multiple: name === 'Features',
        suggestedValues:
          name === 'Features'
            ? category === 'SPORT_SINGLES'
              ? EBAY_SPORT_CARD_FEATURES
              : EBAY_NON_SPORT_CARD_FEATURES
            : (ITEM_SPECIFIC_SUGGESTIONS[name] ?? []),
      },
    ];
  });
}

export function normalizeEbayItemSpecifics(value: unknown): EbayItemSpecifics | undefined {
  if (value === undefined) return {};
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return undefined;
  const normalized: EbayItemSpecifics = {};
  for (const [rawName, rawValues] of Object.entries(value as Record<string, unknown>)) {
    const name = rawName.trim();
    if (name === '' || name.length > 80 || name.includes('|') || !Array.isArray(rawValues))
      return undefined;
    if (rawValues.some((item) => typeof item !== 'string')) return undefined;
    const values = (rawValues as string[]).map((item) => item.trim()).filter((item) => item !== '');
    if (values.length > 30 || values.some((item) => item.length > 200 || item.includes('|')))
      return undefined;
    if (values.length > 0 || name === 'Features') normalized[name] = [...new Set(values)];
  }
  return normalized;
}

export function validateEbayItemSpecifics(
  category: EbaySellerHubSinglesCategory,
  value: EbayItemSpecifics | undefined,
  policy?: EbayCategoryPolicy,
): string[] {
  if (category === 'CCG_SINGLES') return validateEbayCcgPublicSpecifics(policy, value);
  const schema = new Map(ebayItemSpecificSchema(category).map((field) => [field.name, field]));
  const errors: string[] = [];
  for (const [name, values] of Object.entries(value ?? {})) {
    const field = schema.get(name);
    if (field === undefined)
      errors.push(`Item specific "${name}" is not supported by this category.`);
    else if (!field.multiple && values.length > 1)
      errors.push(`Item specific "${name}" accepts only one value.`);
  }
  return errors;
}

function requiredText(value: string | undefined, error: string, errors: string[]): string {
  const normalized = value?.trim() ?? '';
  if (normalized === '') errors.push(error);
  return normalized;
}

function isBaseValue(value: string | undefined): boolean {
  const normalized = value?.trim().toLocaleLowerCase('en-US') ?? '';
  return (
    normalized === '' ||
    normalized === 'base' ||
    normalized === 'no parallel' ||
    normalized === 'no parallel / base version'
  );
}

function isAutographed(identity: CardIdentity): boolean {
  if (identity.autograph === true) return true;
  const source = [identity.attributes, identity.subset, identity.variation]
    .filter((value): value is string => value !== undefined)
    .join(' ')
    .toLocaleLowerCase('en-US');
  return /\bauto(?:graph|graphed)?\b/.test(source);
}

const NCAA = 'National Collegiate Athletic Association (NCAA)';

/**
 * Card categories as eBay Sport values with the most likely League (owner, 2026-10-30: a likely league beats a blank
 * one). College products ("Draft Picks", "Bowman U", NCAA) list as NCAA. Soccer spans too many leagues to guess.
 */
const EBAY_SPORT_BY_CATEGORY: Readonly<
  Record<string, { sport: string; league?: string; collegeLeague?: string }>
> = {
  football: { sport: 'Football', league: 'National Football League (NFL)', collegeLeague: NCAA },
  baseball: { sport: 'Baseball', league: 'Major League Baseball (MLB)', collegeLeague: NCAA },
  basketball: {
    sport: 'Basketball',
    league: 'National Basketball Association (NBA)',
    collegeLeague: NCAA,
  },
  hockey: { sport: 'Ice Hockey', league: 'National Hockey League (NHL)' },
  soccer: { sport: 'Soccer' },
  wnba: { sport: 'Basketball', league: "Women's National Basketball Association (WNBA)" },
};

function ebaySport(identity: CardIdentity): { sport: string; league?: string } {
  const value = identity.category.trim();
  const mapped = EBAY_SPORT_BY_CATEGORY[value.toLocaleLowerCase('en-US')];
  if (mapped === undefined) return { sport: value };
  const text = [identity.set, identity.subset, identity.attributes]
    .filter((part): part is string => part !== undefined)
    .join(' ');
  const college = /\b(draft picks|ncaa|college|collegiate|university|bowman u)\b/i.test(text);
  const league =
    college && mapped.collegeLeague !== undefined ? mapped.collegeLeague : mapped.league;
  return { sport: mapped.sport, ...(league === undefined ? {} : { league }) };
}

/** Panini product lines the catalog records as the brand; eBay's Manufacturer is the company. */
const PANINI_PRODUCT_LINES: ReadonlySet<string> = new Set([
  'absolute',
  'chronicles',
  'contenders',
  'donruss',
  'flawless',
  'immaculate',
  'mosaic',
  'national treasure',
  'national treasures',
  'optic',
  'playoff',
  'prizm',
  'score',
  'select',
  'spectra',
]);

function ebayManufacturer(brand: string | undefined): string {
  const value = brand?.trim() ?? '';
  const normalized = value.toLocaleLowerCase('en-US');
  if (normalized.startsWith('panini') || PANINI_PRODUCT_LINES.has(normalized)) return 'Panini';
  for (const company of ['Topps', 'Upper Deck']) {
    if (normalized.startsWith(`${company.toLocaleLowerCase('en-US')} `)) return company;
  }
  return value;
}

/**
 * The catalog names products "<year> <brand>" with the sport in its own field ("2024 Prizm" + WNBA). Buyers search
 * "2024 Panini Prizm WNBA": add the manufacturer after the leading year, and the league for WNBA products, when the
 * name does not already contain them. Names that already do ("2025 Topps Chrome ...") pass through unchanged.
 */
function ebaySetName(identity: CardIdentity, manufacturer: string): string {
  let name = identity.set.trim();
  const contains = (part: string) =>
    new RegExp(`(^|\\s)${part.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}(\\s|$)`, 'i').test(name);
  if (manufacturer !== '' && !contains(manufacturer)) {
    const year = /^(\d{4}(?:-\d{2,4})?)\s+(.+)$/.exec(name);
    if (year !== null) name = `${year[1]} ${manufacturer} ${year[2]}`;
  }
  if (identity.category.trim().toLocaleLowerCase('en-US') === 'wnba' && !contains('WNBA')) {
    name = `${name} WNBA`;
  }
  return name;
}

/**
 * Values that hold for an ordinary card unless its catalog text says otherwise: Original (not a reprint), not
 * vintage (1990+), standard size, card stock, and a pack autograph certified by the manufacturer.
 */
function ebayStandardValues(identity: CardIdentity, manufacturer: string): Record<string, string> {
  const text = [identity.set, identity.subset, identity.variation, identity.attributes]
    .filter((value): value is string => value !== undefined)
    .join(' ')
    .toLocaleLowerCase('en-US');
  const year = Number.parseInt(identity.year?.trim() ?? '', 10);
  return {
    'C:Original/Licensed Reprint': /\breprint/.test(text) ? '' : 'Original',
    'C:Vintage': Number.isInteger(year) && year >= 1990 ? 'No' : '',
    'C:Card Size': /\b(oversized?|jumbo|box ?toppers?|booklets?|mini|tall ?boy|cabinet)\b/.test(
      text,
    )
      ? ''
      : 'Standard',
    'C:Material': /\b(metal|acetate|clear|wood|leather|canvas|plastic|glass|cloth)\b/.test(text)
      ? ''
      : 'Card Stock',
    'C:Autograph Authentication': isAutographed(identity) ? manufacturer : '',
  };
}

/** Features when the reviewer chose none: the structured suggestions, plus Insert for an insert set. */
function defaultEbayFeatures(
  identity: CardIdentity,
  category: EbaySellerHubSinglesCategory,
): string[] {
  const features = suggestedEbayFeatures(identity, category);
  if (category !== 'CCG_SINGLES' && !isBaseValue(identity.subset)) features.push('Insert');
  return [...new Set(features)];
}

/** Reliable structured defaults; an explicit reviewer Features field remains authoritative. */
export function suggestedEbayFeatures(
  identity: CardIdentity,
  category: EbaySellerHubSinglesCategory,
): string[] {
  if (category === 'CCG_SINGLES') return [];
  const features: string[] = [];
  if (category === 'SPORT_SINGLES' && identity.rookie === true) features.push('Rookie');
  if (identity.serialNumbered === true || identity.serialTo !== undefined)
    features.push('Serial Numbered');
  if (identity.serialTo === 1) features.push('One of One');
  if (identity.memorabilia === true) features.push('Memorabilia');
  if (!isBaseValue(identity.variation)) features.push('Parallel/Variety');
  const supported: ReadonlySet<string> = new Set<string>(
    category === 'SPORT_SINGLES' ? EBAY_SPORT_CARD_FEATURES : EBAY_NON_SPORT_CARD_FEATURES,
  );
  return [...new Set(features)].filter((feature) => supported.has(feature));
}

function formatPrice(amountMinor: number): string {
  return (amountMinor / 100).toFixed(2);
}

function csvCell(value: string): string {
  if (!/[",\r\n]/.test(value)) return value;
  return `"${value.replaceAll('"', '""')}"`;
}

function rowForHeaders(
  headers: readonly string[],
  values: Readonly<Record<string, string>>,
): string {
  return headers.map((header) => csvCell(values[header] ?? '')).join(',');
}

export function ebaySellerHubHeaders(category: EbaySellerHubSinglesCategory): readonly string[] {
  if (category === 'CCG_SINGLES') return EBAY_CCG_SINGLES_HEADERS;
  if (category === 'NON_SPORT_SINGLES') return EBAY_NON_SPORT_SINGLES_HEADERS;
  if (category === 'SPORT_SINGLES') return EBAY_SPORT_SINGLES_HEADERS;
  throw new Error('Unsupported Seller Hub category.');
}

export function validateEbaySellerHubSettings(settings: EbaySellerHubExportSettings): string[] {
  const errors: string[] = [];
  requiredText(settings.location, 'Item location is required.', errors);
  requiredText(settings.paymentProfileName, 'Payment profile name is required.', errors);
  requiredText(settings.paymentProfileId, 'Payment profile ID is required.', errors);
  requiredText(settings.shippingProfileName, 'Shipping profile name is required.', errors);
  requiredText(settings.shippingProfileId, 'Shipping profile ID is required.', errors);
  requiredText(settings.returnProfileName, 'Return profile name is required.', errors);
  requiredText(settings.returnProfileId, 'Return profile ID is required.', errors);
  for (const [value, label] of [
    [settings.paymentProfileId, 'Payment'],
    [settings.shippingProfileId, 'Shipping'],
    [settings.returnProfileId, 'Return'],
  ] as const) {
    if (value.trim() !== '' && !/^\d+$/.test(value.trim())) {
      errors.push(`${label} profile ID must contain only digits.`);
    }
  }
  if (!/^\d{5}(?:-\d{4})?$/.test(settings.location.trim())) {
    errors.push('Item location must be a US ZIP code.');
  }
  if (settings.priceSource === 'CALCULATED_PRICE') {
    errors.push('Calculated pricing is not configured. Choose sale price or price override.');
  }
  if (
    settings.priceSource === 'PRICE_OVERRIDE' &&
    (!Number.isInteger(settings.priceOverrideMinor) || (settings.priceOverrideMinor ?? 0) < 1)
  ) {
    errors.push('A positive price override is required.');
  }
  if (settings.listingType === 'AUCTION' && settings.bestOfferEnabled) {
    errors.push('Best offers are only supported for Buy It Now listings.');
  }
  const acceptPercent = settings.bestOfferAutoAcceptPercent;
  const declinePercent = settings.bestOfferAutoDeclinePercent;
  if (
    acceptPercent !== undefined &&
    (!Number.isFinite(acceptPercent) || acceptPercent <= 0 || acceptPercent > 100)
  ) {
    errors.push('Best-offer auto-accept percent must be greater than 0 and at most 100.');
  }
  if (
    declinePercent !== undefined &&
    (!Number.isFinite(declinePercent) || declinePercent <= 0 || declinePercent > 100)
  ) {
    errors.push('Best-offer auto-decline percent must be greater than 0 and at most 100.');
  }
  if (
    acceptPercent !== undefined &&
    declinePercent !== undefined &&
    declinePercent >= acceptPercent
  ) {
    errors.push('Best-offer auto-decline percent must be lower than auto-accept percent.');
  }
  return errors;
}

export function resolveEbaySellerHubPrice(
  card: EbaySellerHubExportCard,
  settings: EbaySellerHubExportSettings,
): number | undefined {
  if (settings.priceSource === 'PRICE_OVERRIDE') return settings.priceOverrideMinor;
  if (settings.priceSource === 'CALCULATED_PRICE') return undefined;
  return card.amountMinor;
}

export function validateEbaySellerHubCard(
  card: EbaySellerHubExportCard,
  settings: EbaySellerHubResolvedExportSettings,
  policy?: EbayCategoryPolicy,
): EbaySellerHubExportItem {
  const errors = [...validateEbaySellerHubSettings(settings)];
  const warnings: string[] = [];
  requiredText(card.title, 'Listing title is required.', errors);
  if (card.title.length > 80) errors.push('Listing title must be 80 characters or fewer.');
  requiredText(card.description, 'Listing description is required.', errors);
  requiredText(card.identity.category, 'Card category is required.', errors);
  requiredText(card.identity.set, 'Card set is required.', errors);
  requiredText(card.identity.number, 'Card number is required.', errors);
  requiredText(card.identity.name, 'Card name is required.', errors);
  requiredText(card.marketplaceSku, 'Marketplace SKU is required.', errors);
  if (normalizeCardPresentation(card.cardPresentation) === undefined) {
    errors.push('Card presentation must be classified as Raw or Graded.');
  }
  if (settings.category === 'CCG_SINGLES') {
    try {
      errors.push(...validateEbayCcgPublicSpecifics(policy, ccgItemSpecifics(card)));
      mapEbayCategoryPresentation(settings.category, card.cardPresentation, policy);
    } catch (error) {
      errors.push(error instanceof Error ? error.message : 'Invalid CCG category policy.');
    }
  } else {
    errors.push(...validateEbayItemSpecifics(settings.category, card.itemSpecifics));
  }
  if (settings.category === 'NON_SPORT_SINGLES') {
    requiredText(
      settings.franchise ?? card.itemSpecifics?.['Franchise']?.[0],
      'Franchise is required for non-sport singles.',
      errors,
    );
  }
  if (card.marketplaceSku.length > 50)
    errors.push('Marketplace SKU must be 50 characters or fewer.');
  const listingAmountMinor = resolveEbaySellerHubPrice(card, settings);
  if (!Number.isInteger(listingAmountMinor) || (listingAmountMinor ?? 0) < 1) {
    errors.push('A positive reviewed listing price is required.');
  }
  if (card.imageUrls.length < 2) errors.push('Front and back marketplace image URLs are required.');
  if (card.identity.year === undefined) warnings.push('Year will be blank.');
  if (card.identity.brand === undefined) warnings.push('Manufacturer will be blank.');
  return {
    cardId: card.cardId,
    ...(card.shortCode === undefined ? {} : { shortCode: card.shortCode }),
    marketplaceSku: card.marketplaceSku,
    title: card.title,
    errors: [...new Set(errors)],
    warnings,
  };
}

function ccgItemSpecifics(card: EbaySellerHubExportCard): EbayItemSpecifics {
  const language = ebayListingLanguage(card);
  return {
    'Card Name': [card.identity.name.trim()],
    Set: [card.identity.set.trim()],
    'Card Number': [card.identity.number.trim()],
    ...(card.identity.brand === undefined ? {} : { Manufacturer: [card.identity.brand.trim()] }),
    ...(card.identity.year === undefined
      ? {}
      : { 'Year Manufactured': [card.identity.year.trim()] }),
    ...(language === undefined ? {} : { Language: [language] }),
    ...card.itemSpecifics,
  };
}

function sellerHubValues(
  card: EbaySellerHubExportCard,
  settings: EbaySellerHubResolvedExportSettings,
  policy?: EbayCategoryPolicy,
): Record<string, string> {
  const identity = card.identity;
  const parallel = isBaseValue(identity.variation) ? '' : (identity.variation?.trim() ?? '');
  const insertSet = isBaseValue(identity.subset) ? '' : (identity.subset?.trim() ?? '');
  const aspectValues = Object.fromEntries(
    Object.entries(card.itemSpecifics ?? {}).flatMap(([name, values]) => {
      const normalizedName = name.trim();
      const normalizedValue = values
        .map((value) => value.trim())
        .filter(Boolean)
        .join('|');
      if (normalizedName === '' || normalizedValue === '') return [];
      const headers = ebaySellerHubHeaders(settings.category);
      const header =
        headers.find((candidate) => candidate === `C:${normalizedName}`) ??
        headers.find((candidate) => candidate === `*C:${normalizedName}`);
      return header === undefined ? [] : [[header, normalizedValue]];
    }),
  );
  const listingAmountMinor = resolveEbaySellerHubPrice(card, settings) ?? 0;
  const presentation = normalizeCardPresentation(card.cardPresentation);
  const hasReviewedFeatures = Object.prototype.hasOwnProperty.call(
    card.itemSpecifics ?? {},
    'Features',
  );
  const features = (
    hasReviewedFeatures
      ? (card.itemSpecifics?.['Features'] ?? [])
      : defaultEbayFeatures(identity, settings.category)
  ).join('|');
  const presentationValues: Record<string, string> =
    presentation?.kind === 'GRADED'
      ? {
          '*ConditionID': '2750',
          'CD:Professional Grader - (ID: 27501)': EBAY_PROFESSIONAL_GRADER_IDS[presentation.grader],
          'CD:Grade - (ID: 27502)': EBAY_PROFESSIONAL_GRADE_IDS[presentation.grade],
          'CDA:Certification Number - (ID: 27503)': presentation.certificationNumber ?? '',
          'CD:Card Condition - (ID: 40001)': '',
        }
      : {
          '*ConditionID': '4000',
          'CD:Professional Grader - (ID: 27501)': '',
          'CD:Grade - (ID: 27502)': '',
          'CDA:Certification Number - (ID: 27503)': '',
          'CD:Card Condition - (ID: 40001)':
            presentation?.kind === 'RAW' ? EBAY_RAW_CONDITION_IDS[presentation.condition] : '',
        };
  const bestOffersEnabled = settings.listingType === 'BUY_IT_NOW' && settings.bestOfferEnabled;
  const autoAcceptPrice =
    bestOffersEnabled && settings.bestOfferAutoAcceptPercent !== undefined
      ? formatPrice(Math.ceil((listingAmountMinor * settings.bestOfferAutoAcceptPercent) / 100))
      : '';
  const autoDeclinePrice =
    bestOffersEnabled && settings.bestOfferAutoDeclinePercent !== undefined
      ? formatPrice(Math.floor((listingAmountMinor * settings.bestOfferAutoDeclinePercent) / 100))
      : '';
  const common: Record<string, string> = {
    [ACTION_HEADER]: 'Add',
    CustomLabel: card.marketplaceSku,
    '*Title': card.title.trim(),
    ...presentationValues,
    'C:Manufacturer': ebayManufacturer(identity.brand),
    'C:Year Manufactured': identity.year?.trim() ?? '',
    'C:Set': ebaySetName(identity, ebayManufacturer(identity.brand)),
    'C:Parallel/Variety': parallel,
    'C:Autographed': isAutographed(identity) ? 'Yes' : 'No',
    'C:Signed By': isAutographed(identity) ? identity.name.trim() : '',
    'C:Card Number': identity.number.trim(),
    'C:Card Name': identity.name.trim(),
    'C:Language': ebayListingLanguage(card) ?? 'English',
    'C:Insert Set': insertSet,
    'C:Print Run': identity.serialTo === undefined ? '' : String(identity.serialTo),
    ...ebayStandardValues(identity, ebayManufacturer(identity.brand)),
    PicURL: card.imageUrls.join('|'),
    '*Description': card.description?.trim() ?? '',
    '*Format': settings.listingType === 'BUY_IT_NOW' ? 'FixedPrice' : 'Auction',
    '*Duration': settings.listingType === 'BUY_IT_NOW' ? 'GTC' : '7',
    '*StartPrice': formatPrice(listingAmountMinor),
    BestOfferEnabled: bestOffersEnabled ? '1' : '0',
    BestOfferAutoAcceptPrice: autoAcceptPrice,
    MinimumBestOfferPrice: autoDeclinePrice,
    '*Quantity': '1',
    ImmediatePayRequired: settings.listingType === 'BUY_IT_NOW' ? '1' : '0',
    '*Location': settings.location.trim(),
    ShippingProfileID: settings.shippingProfileId.trim(),
    ShippingProfileName: settings.shippingProfileName.trim(),
    ReturnProfileID: settings.returnProfileId.trim(),
    ReturnProfileName: settings.returnProfileName.trim(),
    PaymentProfileID: settings.paymentProfileId.trim(),
    PaymentProfileName: settings.paymentProfileName.trim(),
  };
  if (settings.category === 'CCG_SINGLES') {
    const presentation = mapEbayCategoryPresentation(
      settings.category,
      card.cardPresentation,
      policy,
    );
    const specifics = ccgItemSpecifics(card);
    const errors = validateEbayCcgPublicSpecifics(policy, specifics);
    if (errors.length > 0) throw new Error(errors.join(' '));
    return {
      ...Object.fromEntries(Object.entries(common).filter(([header]) => !/^\*?C:/.test(header))),
      '*Category': '183454',
      '*ConditionID': presentation.conditionId,
      'CD:Professional Grader - (ID: 27501)': presentation.descriptors['27501'] ?? '',
      'CD:Grade - (ID: 27502)': presentation.descriptors['27502'] ?? '',
      'CDA:Certification Number - (ID: 27503)': presentation.descriptors['27503'] ?? '',
      'CD:Card Condition - (ID: 40001)': presentation.descriptors['40001'] ?? '',
      ...Object.fromEntries(
        Object.entries(specifics).map(([name, values]) => [
          name === 'Game' ? '*C:Game' : `C:${name}`,
          values.join('|'),
        ]),
      ),
    };
  }
  if (settings.category === 'NON_SPORT_SINGLES') {
    return {
      ...common,
      '*Category': '183050',
      '*C:Franchise':
        settings.franchise?.trim() || card.itemSpecifics?.['Franchise']?.[0]?.trim() || '',
      'C:Character': identity.name.trim(),
      'C:Type': 'Non-Sport Trading Card',
      ...aspectValues,
      'C:Features': features,
    };
  }
  const sport = ebaySport(identity);
  return {
    ...common,
    '*Category': '261328',
    '*C:Sport': sport.sport,
    'C:League': sport.league ?? '',
    'C:Player/Athlete': identity.name.trim(),
    'C:Season': identity.year?.trim() ?? '',
    'C:Team': identity.team?.trim() ?? '',
    'C:Type': 'Sports Trading Card',
    ...aspectValues,
    'C:Features': features,
  };
}

export function buildEbaySellerHubCsv(
  cards: readonly EbaySellerHubExportCard[],
  settings: EbaySellerHubResolvedExportSettings,
  policy?: EbayCategoryPolicy,
): string {
  if (settings.category === 'CCG_SINGLES') {
    requireEbayCcgPublicPolicy(policy);
    for (const card of cards) {
      const errors = validateEbaySellerHubCard(card, settings, policy).errors;
      if (errors.length > 0) throw new Error(errors.join(' '));
    }
  }
  const headers = ebaySellerHubHeaders(settings.category);
  const info = new Array<string>(headers.length).fill('');
  info[0] = 'Info';
  info[1] = `Version=${EBAY_SELLER_HUB_TEMPLATE_VERSION}`;
  info[2] = 'Template=fx_category_template_EBAY_US';
  const lines = [
    info.map(csvCell).join(','),
    headers.map(csvCell).join(','),
    ...cards.map((card) => rowForHeaders(headers, sellerHubValues(card, settings, policy))),
  ];
  return `\uFEFF${lines.join('\r\n')}\r\n`;
}

/**
 * RFC-0005 Phase 2: one listing as the eBay Listing API receives it. Built from the same reviewed values as the Seller
 * Hub sheet row, so a TCVS-published listing and a Seller Hub upload of the same card cannot differ.
 */
export interface EbayListingPlan {
  sku: string;
  title: string;
  description: string;
  categoryId: string;
  conditionId: string;
  /** Condition descriptor IDs (27501 grader, 27502 grade, 27503 cert number, 40001 card condition). */
  conditionDescriptors: { name: string; values: string[]; additionalInformation?: string }[];
  aspects: EbayItemSpecifics;
  format: 'FIXED_PRICE' | 'AUCTION';
  duration: 'GTC' | 'DAYS_7';
  priceMinor: number;
  quantity: 1;
  bestOffer?: { autoAcceptMinor?: number; autoDeclineMinor?: number };
  postalCode: string;
  policies: { paymentId: string; returnId: string; shippingId: string };
  /** Pictures in listing order (front, back). eBay-hosted (Media API) URLs before a write. */
  imageUrls: string[];
}

export type EbayListingPlanResult =
  | { ok: true; plan: EbayListingPlan }
  | { ok: false; errors: string[] };

export function buildEbayListingPlan(
  card: EbaySellerHubExportCard,
  settings: EbaySellerHubResolvedExportSettings,
  policy?: EbayCategoryPolicy,
): EbayListingPlanResult {
  const validation = validateEbaySellerHubCard(card, settings, policy);
  if (validation.errors.length > 0) return { ok: false, errors: validation.errors };
  const priceMinor = resolveEbaySellerHubPrice(card, settings);
  if (priceMinor === undefined) {
    return { ok: false, errors: ['A calculated price cannot be published through the API.'] };
  }
  let values: Record<string, string>;
  try {
    values = sellerHubValues(card, settings, policy);
  } catch (error) {
    return { ok: false, errors: [error instanceof Error ? error.message : 'Invalid listing.'] };
  }
  const aspects: EbayItemSpecifics = {};
  const conditionDescriptors: EbayListingPlan['conditionDescriptors'] = [];
  for (const [header, value] of Object.entries(values)) {
    const text = value.trim();
    if (text === '') continue;
    const aspect = /^\*?C:(.+)$/.exec(header);
    if (aspect?.[1] !== undefined) {
      aspects[aspect[1]] = text
        .split('|')
        .map((part) => part.trim())
        .filter(Boolean);
      continue;
    }
    const descriptor = /^(CDA?):.*\(ID: (\d+)\)$/.exec(header);
    if (descriptor?.[2] !== undefined) {
      conditionDescriptors.push(
        descriptor[1] === 'CDA'
          ? { name: descriptor[2], values: [], additionalInformation: text }
          : { name: descriptor[2], values: [text] },
      );
    }
  }
  const auction = settings.listingType === 'AUCTION';
  const bestOffer =
    !auction && settings.bestOfferEnabled
      ? {
          ...(settings.bestOfferAutoAcceptPercent === undefined
            ? {}
            : {
                autoAcceptMinor: Math.ceil(
                  (priceMinor * settings.bestOfferAutoAcceptPercent) / 100,
                ),
              }),
          ...(settings.bestOfferAutoDeclinePercent === undefined
            ? {}
            : {
                autoDeclineMinor: Math.floor(
                  (priceMinor * settings.bestOfferAutoDeclinePercent) / 100,
                ),
              }),
        }
      : undefined;
  return {
    ok: true,
    plan: {
      sku: values['CustomLabel'] ?? '',
      title: values['*Title'] ?? '',
      description: values['*Description'] ?? '',
      categoryId: values['*Category'] ?? '',
      conditionId: values['*ConditionID'] ?? '',
      conditionDescriptors,
      aspects,
      format: auction ? 'AUCTION' : 'FIXED_PRICE',
      duration: auction ? 'DAYS_7' : 'GTC',
      priceMinor,
      quantity: 1,
      ...(bestOffer === undefined ? {} : { bestOffer }),
      postalCode: settings.location.trim(),
      policies: {
        paymentId: settings.paymentProfileId.trim(),
        returnId: settings.returnProfileId.trim(),
        shippingId: settings.shippingProfileId.trim(),
      },
      imageUrls: [...card.imageUrls],
    },
  };
}
